<template>
    <announcer 
        :text="text" 
        :prefix="prefix"
        :aria-label="text"
    >
        <div 
            ref="trigger"
            class="announcing-tooltip-wrapper"
            @mouseenter="showTooltip"
            @mouseleave="hideTooltip"
            @click.stop.prevent="handleClick"
            @keydown.enter.stop.prevent="handleClick"
            @keydown.space.stop.prevent="handleClick"
            :aria-describedby="isTooltipVisible ? `tooltip-${_uid}` : null"
        >
            <slot></slot>
        </div>
    </announcer>
</template>

<script>
import announcer from './announcer.vue';

export default {
    name: 'announcing-tooltip',
    components: {
        'announcer': announcer
    },
    props: {
        text: {
            type: String,
            required: true
        },
        prefix: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isTooltipVisible: false,
            tooltipTimeout: null,
            tooltipElement: null
        };
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
        }
        this.removeTooltip();
    },
    methods: {
        createTooltip() {
            if (this.tooltipElement) return;
            
            const triggerRect = this.$refs.trigger.getBoundingClientRect();
            
            this.tooltipElement = document.createElement('div');
            this.tooltipElement.className = 'announcing-tooltip-portal';
            this.tooltipElement.setAttribute('role', 'tooltip');
            this.tooltipElement.setAttribute('id', `tooltip-${this._uid}`);
            this.tooltipElement.textContent = this.text;
            
            // Position the tooltip (only dynamic values need JS)
            this.tooltipElement.style.top = `${triggerRect.top - 40}px`;
            this.tooltipElement.style.left = `${triggerRect.left + triggerRect.width / 2}px`;
            
            document.body.appendChild(this.tooltipElement);
        },
        removeTooltip() {
            if (this.tooltipElement && this.tooltipElement.parentNode) {
                this.tooltipElement.parentNode.removeChild(this.tooltipElement);
                this.tooltipElement = null;
            }
        },
        showTooltip() {
            this.isTooltipVisible = true;
            this.$nextTick(() => {
                this.createTooltip();
            });
        },
        hideTooltip() {
            this.isTooltipVisible = false;
            this.removeTooltip();
            if (this.tooltipTimeout) {
                clearTimeout(this.tooltipTimeout);
                this.tooltipTimeout = null;
            }
        },
        toggleTooltip() {
            if (this.isTooltipVisible) {
                this.hideTooltip();
            } else {
                this.showTooltip();
                this.tooltipTimeout = setTimeout(() => {
                    this.hideTooltip();
                }, 5000);
            }
        },
        handleClick() {
            this.toggleTooltip();
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target) && (!this.tooltipElement || !this.tooltipElement.contains(event.target))) {
                this.hideTooltip();
            }
        }
    }
};
</script>

<style scoped>
.announcing-tooltip-wrapper {
    display: inline-block;
    cursor: pointer;
}

@media (min-width: 577px) {
    .announcing-tooltip-wrapper {
        cursor: default;
    }
}
</style>

<style>
.announcing-tooltip-portal {
    position: fixed;
    transform: translateX(-50%);
    z-index: 100000001;
    background-color: rgba(51, 51, 51, 0.95);
    color: white !important;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    min-width: 60px;
    text-align: center;
}

.announcing-tooltip-portal::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(51, 51, 51, 0.95);
}
</style>